/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any */

import {
  RawGreeksDataMap,
  GREEK_IDX,
  OptionType,
  GreeksData,
  OPTION_IDX,
} from 'types';
import {
  TableData,
  VolatilityDataRow as TableDataRow,
  OptionLegType,
  ModelParameters,
  ProfitLossWithDomain,
  ProfitLossData,
  OptionLeg,
  TimeframeBoundsPoint,
} from 'types/riskEngine';
import * as d3 from 'd3';
import {
  blackScholesMerton,
  calculateImpliedVolatilities,
  calculateVarianceTime,
  loadRiskEngineConstants,
  VarianceTimeCalendarModel,
} from 'sg-risk-engine';
import { PositionLeg, PositionShares } from 'types/optionsCalculator';
import { CLOSURES, dayjs } from './shared/date';
import { convertToDaysTillExpiryIntraday } from './iVol';
import { DEFAULT_DIV_RATE, RISK_FREE_RATE } from './BlackScholes';

const { CALL, PUT } = OPTION_IDX;

export function getStrikePricesForFirstTimestamp(
  currentGreeksObj: any,
): number[] | undefined {
  if (!currentGreeksObj) {
    return undefined;
  }

  const timestamps = Object.keys(currentGreeksObj);
  if (timestamps.length === 0) {
    return undefined;
  }

  const firstTimestamp = timestamps[0];
  const firstTimestampObj = currentGreeksObj[firstTimestamp];
  const strikePrices = Object.keys(firstTimestampObj).map(Number);
  return strikePrices;
}

// Return DTE, price, column assuming the first timestamp is 0 DTE after getFilteredGreeksData
export function extractTableData(
  data: RawGreeksDataMap,
  isCall: boolean,
  column: number,
): TableData[] {
  const result: TableData[] = [];

  for (const tte in data) {
    const priceLevel = data[tte];
    const timestampResult: TableData = {
      tte: Number(tte),
      strikePrices: {},
    };

    for (const price in priceLevel) {
      const vol = priceLevel[price][isCall ? 0 : 1][column];
      timestampResult.strikePrices[Number(price)] = vol;
    }

    result.push(timestampResult);
  }

  return result;
}

export function getVolatilityData(
  data: RawGreeksDataMap | null,
  optionType: OptionType,
  currentPrice: number | undefined,
): TableData[] {
  if (!data || !currentPrice) {
    return [];
  }

  const result: TableData[] = [];

  for (const tte in data) {
    const priceLevel = data[tte];
    const timestampResult: TableData = {
      tte: Number(tte),
      strikePrices: {},
    };

    const strikeMapEntries: [string, GreeksData][] = Object.entries(priceLevel);
    const strikes = strikeMapEntries.map(([strike, _]) => Number(strike));
    const prices = strikeMapEntries.map(([_, greeks]) => {
      const idx = optionType === OptionType.CALL ? CALL : PUT;
      return greeks[idx][GREEK_IDX.PRICE];
    });

    const daysTillExpiration = convertToDaysTillExpiryIntraday(
      dayjs().valueOf(),
      Number(tte),
    );

    const yte = calculateVarianceTime(
      dayjs(Number(tte)).unix(),
      Array.from(CLOSURES),
      daysTillExpiration * 24 * 60 * 60,
      VarianceTimeCalendarModel.BusinessDayModel,
    );

    const ivs = calculateImpliedVolatilities(
      currentPrice,
      new Float64Array(strikes),
      new Float64Array(prices),
      yte,
      RISK_FREE_RATE,
      DEFAULT_DIV_RATE,
      dayjs().unix(),
      Array.from(CLOSURES),
      false,
    );

    strikes.forEach((strike, i) => {
      timestampResult.strikePrices[strike] = ivs[i];
    });

    result.push(timestampResult);
  }

  return result;
}

// Optimized interpolation over a row given a sequence of sorted prices
export function interpolateDataRow(
  data: TableDataRow[],
  prices: number[],
): number[] {
  let i = 0;

  return prices.map((price) => {
    while (i + 1 < data.length && data[i + 1].strike <= price) {
      ++i;
    }

    if (i + 1 >= data.length) {
      // clamp right
      return data[data.length - 1].value;
    } else if (price < data[0].strike) {
      // clamp left
      return data[0].value;
    }

    const fraction =
      (price - data[i].strike) / (data[i + 1].strike - data[i].strike);
    return d3.interpolateNumber(data[i].value, data[i + 1].value)(fraction);
  });
}

export function interpolateDataTable(
  data: TableData[],
  prices: number[],
  dte: number,
): number[] {
  const r0 = Math.floor(dte);
  const r1 = r0 + 1;

  if (data.length === 0) {
    return prices.map(() => 0);
  } else if (r0 < 0) {
    const vol0 = data[0];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  } else if (r1 >= data.length) {
    const vol0 = data[data.length - 1];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  }

  const vol0 = data[r0];
  const vol1 = data[r1];

  const row0: TableDataRow[] = Object.keys(vol0.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol0.strikePrices[Number(strike)],
  }));

  const row1: TableDataRow[] = Object.keys(vol1.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol1.strikePrices[Number(strike)],
  }));

  const w1 = dte - r0;
  const w0 = 1 - w1;

  const vol0Interpolated = interpolateDataRow(row0, prices);
  const vol1Interpolated = interpolateDataRow(row1, prices);

  const result: number[] = vol0Interpolated.map(
    (v, i) => v * w0 + vol1Interpolated[i] * w1,
  );

  return result;
}

/**
 * Interpolates volatility data for a specific expiration date across multiple price points.
 *
 * This function performs temporal interpolation of implied volatility data by finding the closest
 * volatility surface data points to the target expiration and interpolating between them.
 * It's essential for options pricing calculations where we need volatility estimates for
 * prices that may not have exact matches in our volatility data for a given expiration date.
 *
 * @param data - Array of volatility surface data, each containing strike prices and their
 *               corresponding implied volatilities for different time-to-expiration values
 * @param prices - Array of underlying asset prices for which we want volatility interpolation
 * @param expiration - Target expiration timestamp (in milliseconds) for which we need volatilities
 *
 * @returns Array of interpolated volatility values corresponding to each price in the prices array.
 *          Returns array of zeros if no data is available or expiration is not found.
 *
 * @example
 * // Get volatilities for prices [100, 105, 110] at a specific expiration
 * const vols = getInterpolatedVolatility(volData, [100, 105, 110], 1640995200000);
 *
 * Algorithm:
 * 1. Finds the exact match for the expiration in the volatility data
 * 2. If exact match found, uses that data point and the next one for interpolation
 * 3. If no exact match, falls back to boundary conditions (first or last available data)
 * 4. Performs temporal interpolation between two volatility surfaces
 * 5. For each surface, performs strike-price interpolation using interpolateDataRow
 * 6. Combines results using weighted average based on time distance
 */
export function getInterpolatedVolatility(
  data: TableData[],
  prices: number[],
  expiration: number,
): number[] {
  const dte = convertToDaysTillExpiryIntraday(dayjs().valueOf(), expiration);

  const r0 = data.findIndex((d) => d.tte === expiration);
  const r1 = r0 + 1;

  if (data.length === 0 || r0 === -1) {
    return prices.map(() => 0);
  } else if (r0 < 0) {
    const vol0 = data[0];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  } else if (r1 >= data.length) {
    const vol0 = data[data.length - 1];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  }

  const vol0 = data[r0];
  const vol1 = data[r1];

  const dte0 = convertToDaysTillExpiryIntraday(dayjs().valueOf(), vol0.tte);

  const row0: TableDataRow[] = Object.keys(vol0.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol0.strikePrices[Number(strike)],
  }));

  const row1: TableDataRow[] = Object.keys(vol1.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol1.strikePrices[Number(strike)],
  }));

  const w1 = dte - dte0;
  const w0 = 1 - w1;

  const vol0Interpolated = interpolateDataRow(row0, prices);
  const vol1Interpolated = interpolateDataRow(row1, prices);

  const result: number[] = vol0Interpolated.map(
    (v, i) => v * w0 + vol1Interpolated[i] * w1,
  );

  return result;
}

export function generatePrices(
  minValue: number,
  maxValue: number,
  numSteps: number = 1_000,
): number[] {
  // Calculate minimum required steps to keep stepSize >= 0.01
  const minRequiredSteps = Math.ceil((maxValue - minValue) / 0.01);
  const adjustedNumSteps = Math.min(numSteps, minRequiredSteps);

  const stepSize = (maxValue - minValue) / (adjustedNumSteps - 1);
  return Array.from(
    { length: adjustedNumSteps },
    (_, i) => minValue + i * stepSize,
  );
}

export function isCall(type: OptionLegType): boolean {
  return [OptionLegType.SHORT_CALL, OptionLegType.LONG_CALL].includes(type);
}

export function isLong(type: OptionLegType): boolean {
  return [OptionLegType.LONG_PUT, OptionLegType.LONG_CALL].includes(type);
}

export function calculateOptionLegProfit(
  { legType, strikePrice, shares, premium, daysToExpiration }: OptionLeg,
  currentStockPrice: number,
  modelParameters: ModelParameters,
  statisticalVolatility: number,
): number {
  const simplePrice = isCall(legType)
    ? Math.max(0, currentStockPrice - strikePrice)
    : Math.max(0, strikePrice - currentStockPrice);

  const riskEngineConstants = loadRiskEngineConstants();

  const bsm = blackScholesMerton(
    currentStockPrice,
    strikePrice,
    daysToExpiration / riskEngineConstants.ADJUSTED_DAYS_IN_YEAR,
    modelParameters.riskFreeRate,
    0,
    modelParameters.volatility ?? statisticalVolatility,
    isCall(legType),
  );

  const price = daysToExpiration > 0 ? bsm.price : simplePrice;
  const profit = isLong(legType)
    ? price * shares - premium
    : premium - price * shares;

  return profit;
}

export function getOptionLegProfit(
  leg: PositionLeg,
  currentStockPrice: number,
  modelParameters: ModelParameters,
  statisticalVolatility: number,
): number {
  const simplePrice =
    leg.type === OptionType.CALL
      ? Math.max(0, currentStockPrice - leg.strike)
      : Math.max(0, leg.strike - currentStockPrice);

  const riskEngineConstants = loadRiskEngineConstants();

  const fromDate = dayjs().valueOf();
  const toDate = leg.expiration;
  const dte = convertToDaysTillExpiryIntraday(fromDate, toDate);

  const bsm = blackScholesMerton(
    currentStockPrice,
    leg.strike,
    dte / riskEngineConstants.ADJUSTED_DAYS_IN_YEAR,
    modelParameters.riskFreeRate,
    0,
    modelParameters.volatility ?? statisticalVolatility,
    leg.type === OptionType.CALL,
  );

  const currentPrice = dte > 0 ? bsm.price : simplePrice;
  const originalPremium = leg.price;

  // For long positions (quantity > 0): profit = (current_price - original_price) * quantity
  // For short positions (quantity < 0): profit = (original_price - current_price) * |quantity|
  const profit =
    leg.quantity > 0
      ? (currentPrice - originalPremium) * leg.quantity
      : (originalPremium - currentPrice) * Math.abs(leg.quantity);

  return profit * 100; // convert to dollars since option prices are multiples of 100
}

export function getShareProfit(
  share: PositionShares,
  currentStockPrice: number,
): number {
  const currentValue = share.quantity * currentStockPrice;
  const costBasis = share.quantity * share.pricePaid;
  return currentValue - costBasis;
}

export function getSharesProfit(
  shares: PositionShares[],
  currentStockPrice: number,
): number {
  return shares.reduce((acc, share) => {
    return acc + getShareProfit(share, currentStockPrice);
  }, 0);
}

function getBounds(
  timeframes: TimeframeBoundsPoint[],
  daysToExpiry: number,
): number[] {
  if (!timeframes || timeframes.length === 0) {
    return [0, 0];
  }

  if (daysToExpiry <= timeframes[0].daysToExpiry) {
    return timeframes[0]['bounds-timeframe'];
  }

  for (let i = 0; i < timeframes.length - 1; i++) {
    if (
      daysToExpiry >= timeframes[i].daysToExpiry &&
      daysToExpiry <= timeframes[i + 1].daysToExpiry
    ) {
      const w =
        (daysToExpiry - timeframes[i].daysToExpiry) /
        (timeframes[i + 1].daysToExpiry - timeframes[i].daysToExpiry);

      const b0 =
        timeframes[i]['bounds-timeframe'][0] +
        (timeframes[i + 1]['bounds-timeframe'][0] -
          timeframes[i]['bounds-timeframe'][0]) *
          w;
      const b1 =
        timeframes[i]['bounds-timeframe'][1] +
        (timeframes[i + 1]['bounds-timeframe'][1] -
          timeframes[i]['bounds-timeframe'][1]) *
          w;

      return [Math.min(b0, b1), Math.max(b0, b1)];
    }
  }

  return timeframes[timeframes.length - 1]['bounds-timeframe'];
}

export function calculateProfitLoss(
  optionLegs: OptionLeg[],
  volDataCall: TableData[],
  volDataPut: TableData[],
  modelParameters: ModelParameters,
  priceDomain: number[] | null,
  timeframes: TimeframeBoundsPoint[] | null,
): ProfitLossWithDomain {
  if (optionLegs.length === 0) {
    return {
      profitLossData: [],
      domain: [0, 0],
    };
  }

  const maxStrikePrice = Math.max(
    ...optionLegs.map((option) => option.strikePrice),
  );

  const prices = priceDomain
    ? generatePrices(priceDomain[0], priceDomain[1])
    : generatePrices(0, 2 * maxStrikePrice);

  const priceVolCalls = optionLegs.map((optionLeg) =>
    interpolateDataTable(volDataCall, prices, optionLeg.daysToExpiration),
  );

  const priceVolPuts = optionLegs.map((optionLeg) =>
    interpolateDataTable(volDataPut, prices, optionLeg.daysToExpiration),
  );

  const maxDaysToExpiration = Math.max(
    ...optionLegs.map((optionLeg) => optionLeg.daysToExpiration),
  );
  const ivBounds = timeframes
    ? getBounds(timeframes, maxDaysToExpiration)
    : null;

  const allProfitLossData: ProfitLossData[] = prices.map((price, index) => {
    let profit: number = 0.0;
    optionLegs.forEach((optionLeg, optionIndex) => {
      profit += calculateOptionLegProfit(
        optionLeg,
        price,
        modelParameters,
        (isCall(optionLeg.legType)
          ? priceVolCalls[optionIndex]
          : priceVolPuts[optionIndex])[index],
      );
    });

    const profitBounds = [0.0, 0.0];
    if (ivBounds) {
      optionLegs.forEach((optionLeg, optionIndex) => {
        profitBounds[0] += calculateOptionLegProfit(
          optionLeg,
          price,
          { ...modelParameters, volatility: ivBounds[0] },
          (isCall(optionLeg.legType)
            ? priceVolCalls[optionIndex]
            : priceVolPuts[optionIndex])[index],
        );
        profitBounds[1] += calculateOptionLegProfit(
          optionLeg,
          price,
          { ...modelParameters, volatility: ivBounds[1] },
          (isCall(optionLeg.legType)
            ? priceVolCalls[optionIndex]
            : priceVolPuts[optionIndex])[index],
        );
      });
    } else {
      profitBounds[0] = profit;
      profitBounds[1] = profit;
    }

    return {
      price,
      profit,
      volatility:
        modelParameters.volatility ??
        (priceVolCalls.length > 0 ? priceVolCalls[0][index] : 0),
      profitBounds,
    };
  });

  // Domain is defined as the region where the PnL line is profit->loss or loss->profit.
  const getProfitDomain = (profitLossData: ProfitLossData[]): number[] => {
    let minDomain: number | null = null;
    let maxDomain: number | null = null;

    for (let i = 0; i < profitLossData.length - 1; i++) {
      if (
        (profitLossData[i].profit >= 0 && profitLossData[i + 1].profit < 0) ||
        (profitLossData[i].profit < 0 && profitLossData[i + 1].profit >= 0)
      ) {
        if (minDomain === null) {
          minDomain = profitLossData[i].price;
        } else {
          maxDomain = profitLossData[i + 1].price;
        }
      }
    }

    if (minDomain === null || maxDomain === null) {
      return [
        0,
        profitLossData.length > 0
          ? profitLossData[profitLossData.length - 1].price
          : 0,
      ];
    }

    let domainWindow = maxDomain - minDomain;
    domainWindow = Math.max(1, domainWindow * 0.15); // MINIMUM_CHART_X_DOMAIN placeholder
    minDomain -= domainWindow;
    maxDomain += domainWindow;

    minDomain = Math.floor(minDomain);
    maxDomain = Math.ceil(maxDomain);

    if (minDomain < 0) {
      minDomain = 0;
    }

    return [minDomain, maxDomain];
  };

  const [minPriceDomain, maxPriceDomain] = getProfitDomain(allProfitLossData);
  const chartProfitLossData = allProfitLossData.filter(
    (data) => data.price >= minPriceDomain && data.price <= maxPriceDomain,
  );

  return {
    profitLossData: chartProfitLossData,
    domain: [minPriceDomain, maxPriceDomain],
  };
}

export function getProfitLoss(
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  volDataCall: TableData[],
  volDataPut: TableData[],
  currentPrice: number,
  modelParameters: ModelParameters,
): ProfitLossWithDomain {
  if (optionLegs.length === 0) {
    return {
      profitLossData: [],
      domain: [0, 0],
    };
  }

  const maxStrikePrice = Math.max(...optionLegs.map((option) => option.strike));
  const minStrikePrice = Math.min(...optionLegs.map((option) => option.strike));

  // default to 25% of the range on each side
  const domain = [Math.max(0, minStrikePrice * 0.75), maxStrikePrice * 1.25];

  const prices = generatePrices(domain[0], domain[1]);

  const priceVolCalls = optionLegs.map((optionLeg) =>
    getInterpolatedVolatility(volDataCall, prices, optionLeg.expiration),
  );

  const priceVolPuts = optionLegs.map((optionLeg) =>
    getInterpolatedVolatility(volDataPut, prices, optionLeg.expiration),
  );

  const allProfitLossData: ProfitLossData[] = prices.map((price, index) => {
    let profit: number = 0.0;
    optionLegs.forEach((optionLeg, optionIndex) => {
      profit += getOptionLegProfit(
        optionLeg,
        price,
        modelParameters,
        (price > currentPrice
          ? priceVolCalls[optionIndex]
          : priceVolPuts[optionIndex])[index],
      );
    });

    profit += getSharesProfit(shares, price);

    const profitBounds = [profit, profit];

    return {
      price,
      profit,
      profitBounds,
    };
  });

  const chartProfitLossData = allProfitLossData.filter(
    (data) => data.price >= domain[0] && data.price <= domain[1],
  );

  return {
    profitLossData: chartProfitLossData,
    domain,
  };
}
